import dayjs from 'dayjs';
import { Trans } from '@lingui/macro';
import TransactionLink from "bukku/components/TransactionLink";
import React from 'react';
import TransactionType from "bukku/components/TransactionType";
import AmountTd from "bukku/components/Table/Common/AmountTd";
import FormatNumber from "../../../../../../components/FormatNumber";
import BukkuTooltip from "bukku/components/Tooltip";
import ContactName from "bukku/components/Columns/Reports/Common/ContactName";

const nonHeadingCols = [
    'date',
    'number',
    'type',
    'contact_name',
    'description',
];

function getRowClassName(rowType) {
    switch (rowType) {
        case 'spacer':
            return 'spacer-col';
        case 'heading':
            return 'heading-col';
        case 'type_total':
            return 'subtotal-col';
        default:
            return '';
    }
}

function getColClassName(rowType, colKey) {
    if (['spacer', 'heading'].includes(rowType))
        return '';

    switch (colKey) {
        case 'date':
            return 'indent-col';
        default:
            return '';
    }
}

function getColSpan(rowType, colKey, columns = []) {
    switch (rowType) {
        case 'type_total':
            switch (colKey) {
                case 'date':
                    return columns.filter(col => nonHeadingCols.includes(col?.key)).length;
                case 'quantity':
                case 'unit_price':
                case 'amount':
                case 'discount_amount':
                case 'tax_amount':
                case 'net_amount':
                    return 1;
                default:
                    return 0;
            }
        case 'spacer':
        case 'heading':
            switch (colKey) {
                case 'date':
                    return columns.length;
                default:
                    return 0;
            }
        default:
            return 1;
    }
}

function getStyle(columnDiv, idx, colSpan, allColumns) {
    const columnsToMerge = allColumns.filter((_, colIdx) => colIdx >= idx && colIdx < idx + colSpan);
    const flexGrow = columnsToMerge.reduce((acc, column) => acc + (column.flexGrow || 0), 0);

    return {
        ...(columnDiv?.props?.style || {}),
        // paddingLeft: 8,
        // paddingRight: 4 * (2 * colSpan - 1),
        paddingLeft: 17.5,
        paddingRight: 7.5 * (2 * colSpan - 1),
        flexGrow,
    };
}

export const Helper = {
    renderDate: ({ rowData }) => {
        switch (rowData.rowType) {
            case 'spacer':
                return null;
            case 'type_total':
                return <Trans>Total</Trans>;
            case 'heading':
                return rowData.label;
            default: {
                const label = dayjs(rowData.date).format('DD/MM/YYYY');
                return (
                    <TransactionLink transaction={rowData} label={label} />
                );
            }
        }
    },

    renderNumber: ({ rowData }) => {
        if (rowData.type === 'opening_balance') {
            return (
                <div className="BaseTable__row-cell-text"><Trans>Opening Balance</Trans></div>
            );
        }
        if (rowData.type === 'unrealised_gain_loss') {
            return (
                <div className="BaseTable__row-cell-text"><Trans>Unrealised Gain</Trans></div>
            );
        }

        return (
            <div className="BaseTable__row-cell-text">
                {rowData.number || '-'}
            </div>
        );
    },

    renderType: ({ rowData }) => (
        <div className="BaseTable__row-cell-text">
            <TransactionType record={rowData} />
        </div>
    ),

    renderContact: ({ rowData }) => {
        if (rowData.type === 'opening_balance')
            return null;

        const { contact_id, contact_name, contact_code } = rowData;
        return (
            <ContactName
                contact_id={contact_id}
                contact_name={contact_name || '-'}
                contact_code={contact_code || ''}
            />
        );
    },

    renderContactCode: ({ rowData }) => (
        <div className="BaseTable__row-cell-text">
            {rowData.contact_code}
        </div>
    ),

    renderQuantity: ({ rowData }) => (
        <div className="BaseTable__row-cell-text">
            <FormatNumber amount={rowData.quantity} dp={2} dp2={4} />
        </div>
    ),

    renderAmount: (amount, currency_symbol) => {
        if (amount === '' || amount === undefined || amount === null)
            return null;

        return (
            <AmountTd currency_symbol={currency_symbol} amount={amount} />
        );
    },

    renderDescription: ({ rowData }) => {
        if (rowData.type === 'opening_balance')
            return null;

        if (rowData.colType === 'subtotal')
            return '';

        return (
            <div className="BaseTable__row-cell-text">
                <BukkuTooltip
                    title={rowData.description}
                    // autoAdjustOverflow={false}
                    placement="topLeft"
                >
                    {rowData.description || '-'}
                </BukkuTooltip>
            </div>
        );
    },

    rowRenderer: props => {
        const {
            columns,
            cells: columnDivs,
            rowData: record,
        } = props;

        const rowClassName = getRowClassName(record.rowType);
        return columnDivs.map((columnDiv, idx) => {
            const column = columns[idx] || {};

            const colSpan = getColSpan(record.rowType, column.key, columns);
            const colClassName = getColClassName(record.rowType, column.key);

            if (colSpan === 0)
                return null;
            if (colSpan === 1) {
                return React.cloneElement(columnDiv, {
                    className: `${columnDiv.props.className} ${colClassName} ${rowClassName}`,
                    children: columnDiv?.props?.children,
                    style: { ...(columnDiv?.props?.style || {}), flexGrow: (column.flexGrow || 0) },
                });
            }
            return React.cloneElement(columnDiv, {
                className: `${columnDiv.props.className} ${colClassName} ${rowClassName}`,
                children: columnDiv?.props?.children,
                style: getStyle(columnDiv, idx, colSpan, columns),
            });
        });
    },
};
